import { usePrincipal } from '@/core/auth/appToken';

/**
 * Display account info for the header ("Angemeldet als …"). Kept as its own interface (rather
 * than reusing `Principal`) because it is a UI-display concept, not the auth/guard contract.
 */
export interface AuthAccount {
  readonly displayName: string;
}

/**
 * Currently authenticated account for display purposes only, derived from the already-resolved
 * {@link Principal} in the app-token store (no extra roundtrip). Resolves to `null` when no
 * principal has been resolved yet (e.g. before the token exchange completes). `displayName` falls
 * back to an empty string when `Principal.userDisplayName` is absent — the contract marks it
 * nullable (existing users may not have it resolved yet) — so callers never render the literal
 * `"undefined"`.
 */
export function useAuthAccount(): AuthAccount | null {
  const principal = usePrincipal();
  if (principal === null) {
    return null;
  }
  return { displayName: principal.userDisplayName ?? '' };
}

/**
 * True once the native-auth token exchange has resolved a {@link Principal} for this session.
 * Replaces the former msal-react `useIsAuthenticated` (and the Demo-Login override) as the
 * single source of truth for auth state — consumed by `RequireAuth`.
 */
export function useAuthIsAuthenticated(): boolean {
  return usePrincipal() !== null;
}
